/**
 * Furrocio — Product Modal Swipe Navigation (Mobile)
 * Adds horizontal swipe-to-navigate between product images
 * inside the product media modal on mobile devices.
 *
 * HOW TO INSTALL:
 * 1. Upload this file to your theme's Assets folder (replace the old one)
 * 2. In your theme.liquid, add before </body>:
 *    <script src="{{ 'modal-swipe.js' | asset_url }}" defer="defer"></script>
 */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 749;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function setupSwipe(modal) {
    if (!isMobile()) return;

    const content = modal.querySelector('.product-media-modal__content');
    if (!content) return;

    // The images are direct <img> children inside __content
    const images = Array.from(content.querySelectorAll('img'));
    if (images.length < 2) return;

    // Avoid double-init
    if (content.dataset.swipeInit === 'true') return;
    content.dataset.swipeInit = 'true';

    // --- Wrap each image in a slide container ---
    images.forEach((img) => {
      const slide = document.createElement('div');
      slide.className = 'modal-swipe-slide';
      slide.style.cssText = `
        flex: 0 0 100%;
        width: 100%;
        min-width: 100%;
        scroll-snap-align: start;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        overflow: hidden;
      `;
      img.parentNode.insertBefore(slide, img);
      slide.appendChild(img);

      img.style.width = '100%';
      img.style.height = 'auto';
      img.style.objectFit = 'contain';
      img.style.maxHeight = '80vh';
    });

    // --- Convert content to horizontal slider ---
    content.style.cssText = `
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      height: 100%;
      gap: 0;
      padding: 0;
      scroll-behavior: smooth;
    `;

    // Hide scrollbar
    const styleId = 'modal-swipe-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .product-media-modal__content::-webkit-scrollbar {
          display: none;
        }
        .product-media-modal__content {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `;
      document.head.appendChild(style);
    }

    // --- Scroll to the "active" image (the one with .active class) ---
    const activeImg = content.querySelector('img.active');
    if (activeImg) {
      const activeSlide = activeImg.closest('.modal-swipe-slide');
      if (activeSlide) {
        requestAnimationFrame(() => {
          content.scrollTo({ left: activeSlide.offsetLeft, behavior: 'instant' });
          updateCounter();
        });
      }
    }

    // --- Add counter indicator ---
    let counter = modal.querySelector('.modal-swipe-counter');
    if (!counter) {
      counter = document.createElement('div');
      counter.className = 'modal-swipe-counter';
      counter.style.cssText = `
        position: fixed;
        bottom: 28px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.55);
        color: #fff;
        font-size: 13px;
        font-family: var(--font-body-family, sans-serif);
        padding: 5px 14px;
        border-radius: 20px;
        z-index: 10001;
        letter-spacing: 0.5px;
        pointer-events: none;
      `;
      modal.appendChild(counter);
    }

    const slides = content.querySelectorAll('.modal-swipe-slide');

    function updateCounter() {
      const scrollLeft = content.scrollLeft;
      const itemWidth = content.clientWidth;
      if (itemWidth === 0) return;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      counter.textContent = `${currentIndex + 1} / ${slides.length}`;
    }

    content.addEventListener('scroll', updateCounter, { passive: true });
    updateCounter();

    // --- Prevent modal from closing on swipe ---
    let touchStartX = 0;
    let touchStartY = 0;
    let isSwiping = false;

    content.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isSwiping = false;
    }, { passive: true });

    content.addEventListener('touchmove', (e) => {
      const dx = Math.abs(e.touches[0].clientX - touchStartX);
      const dy = Math.abs(e.touches[0].clientY - touchStartY);
      if (dx > 10 || dy > 10) {
        isSwiping = true;
      }
    }, { passive: true });

    content.addEventListener('click', (e) => {
      if (isSwiping) {
        e.stopPropagation();
        isSwiping = false;
      }
    }, true);

    content.addEventListener('pointerup', (e) => {
      if (isSwiping) {
        e.stopPropagation();
      }
    }, true);

    // --- Clean up when modal closes ---
    const cleanupObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open' && !modal.hasAttribute('open')) {
          content.querySelectorAll('.modal-swipe-slide').forEach((slide) => {
            const img = slide.querySelector('img');
            if (img) {
              slide.parentNode.insertBefore(img, slide);
            }
            slide.remove();
          });

          content.style.cssText = '';
          content.dataset.swipeInit = 'false';

          if (counter) counter.remove();
          cleanupObserver.disconnect();
        }
      });
    });
    cleanupObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  function initModalSwipe() {
    // Watch for product-modal elements getting the 'open' attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const target = mutation.target;
        if (
          mutation.attributeName === 'open' &&
          target.tagName &&
          target.tagName.toLowerCase() === 'product-modal' &&
          target.hasAttribute('open')
        ) {
          requestAnimationFrame(() => setupSwipe(target));
        }
      });
    });

    // Observe existing product-modal elements
    document.querySelectorAll('product-modal').forEach((modal) => {
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    });

    // Also watch for dynamically added product-modal elements
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.tagName.toLowerCase() === 'product-modal') {
              observer.observe(node, { attributes: true, attributeFilter: ['open'] });
            }
            node.querySelectorAll?.('product-modal')?.forEach((modal) => {
              observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
            });
          }
        });
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModalSwipe);
  } else {
    initModalSwipe();
  }
})();